import { create } from 'zustand';
import useItemStore from '@/stores/useItem';

/**
 * Target message when composing a reply in member chat.
 */
export type MemberChatReplyTarget = {
  messageId: number;
  authorName: string;
  previewText: string;
};

/**
 * Indicates whether a jump target is expected in the live feed or archived vault.
 */
export type MemberChatJumpSource = 'live' | 'vault';

/**
 * Pending inbox/notification jump target for member chat scroll + highlight.
 */
export type MemberChatJumpTarget = {
  messageId: string;
  source?: MemberChatJumpSource;
};

/**
 * Chat store for managing chat states
 * 
 * Handles both AI Chat (layout level) and Member Chat (folder level)
 */
type ChatStore = {
  /**
   * Whether the AI Chat drawer is open
   */
  isAIChatOpen: boolean;
  /**
   * Whether the AI Chat bottom panel is open
   */
  isAIBottomChatOpen: boolean;
  /**
   * Bottom AI Chat panel height in pixels
   */
  aiBottomChatHeightPx: number;
  /**
   * Active AI conversation id for the side drawer chat.
   */
  aiDrawerConversationId: string | null;
  /**
   * Active AI conversation id for the bottom chat panel.
   */
  aiBottomConversationId: string | null;
  /**
   * Whether the Member Chat drawer is open
   */
  isMemberChatOpen: boolean;
  /**
   * Current folder ID for member chat context
   */
  currentFolderId: string | null;
  /**
   * Draft text for the AI chat composer (Markdown), filled from TipTap "Share to AI Chat".
   */
  aiChatDraftMessage: string;
  /**
   * Bumped when draft is pushed from outside the composer so the UI can focus the input.
   */
  aiChatDraftFocusRequestId: number;
  /**
   * Draft text for the member chat composer (Markdown), filled from TipTap "Share to Member Chat".
   */
  memberChatDraftMessage: string;
  /**
   * Bumped when member chat draft is pushed from outside the composer so the UI can focus the input.
   */
  memberChatDraftFocusRequestId: number;
  /**
   * Message being replied to in member chat (distinct from list-item reply via `useItemStore.commentId`).
   */
  memberChatReplyTarget: MemberChatReplyTarget | null;
  /**
   * Pending member chat message to scroll to and highlight (from inbox/notifications).
   */
  jumpToMessage: MemberChatJumpTarget | null;
  /**
   * Set AI Chat open state
   */
  setAIChatOpen: (open: boolean) => void;
  /**
   * Set AI bottom chat open state
   */
  setAIBottomChatOpen: (open: boolean) => void;
  /**
   * Set AI bottom chat panel height in pixels
   */
  setAIBottomChatHeightPx: (heightPx: number) => void;
  /**
   * Set active conversation for the side drawer chat.
   */
  setAIDrawerConversationId: (conversationId: string | null) => void;
  /**
   * Set active conversation for the bottom chat panel.
   */
  setAIBottomConversationId: (conversationId: string | null) => void;
  /**
   * Set Member Chat open state
   */
  setMemberChatOpen: (open: boolean) => void;
  /**
   * Set current folder ID for member chat
   */
  setCurrentFolderId: (folderId: string | null) => void;
  /**
   * Replace the AI chat composer draft (no focus change; used while typing).
   */
  setAIChatDraftMessage: (text: string) => void;
  /**
   * Replace the draft from TipTap share and focus the AI chat input.
   */
  pushAIChatDraftMessage: (text: string) => void;
  /**
   * Append Markdown to the AI chat draft (adds a blank line when the composer is not empty).
   */
  appendAIChatDraftMessage: (text: string) => void;
  /**
   * Clear the AI chat composer draft.
   */
  clearAIChatDraftMessage: () => void;
  /**
   * Replace the member chat composer draft (no focus change; used while typing).
   */
  setMemberChatDraftMessage: (text: string) => void;
  /**
   * Replace the draft from TipTap share and focus the member chat input.
   */
  pushMemberChatDraftMessage: (text: string) => void;
  /**
   * Append Markdown to the member chat draft (adds a blank line when the composer is not empty).
   */
  appendMemberChatDraftMessage: (text: string) => void;
  /**
   * Clear the member chat composer draft.
   */
  clearMemberChatDraftMessage: () => void;
  /**
   * Focus the member chat composer (e.g. Open Group Root, item comment).
   */
  requestMemberChatComposerFocus: () => void;
  /**
   * Set the message reply target and focus the member chat composer.
   */
  setMemberChatReplyTarget: (target: MemberChatReplyTarget) => void;
  /**
   * Clear the message reply target.
   */
  clearMemberChatReplyTarget: () => void;
  /**
   * Queue scroll/highlight for a member chat message.
   */
  requestMemberChatJumpToMessage: (
    messageId: string | number,
    source?: MemberChatJumpSource,
  ) => void;
  /**
   * Clear pending member chat jump target.
   */
  clearMemberChatJumpToMessage: () => void;
  /**
   * Toggle AI Chat open state
   */
  toggleAIChat: () => void;
  /**
   * Toggle AI bottom chat open state
   */
  toggleAIBottomChat: () => void;
  /**
   * Toggle Member Chat open state
   */
  toggleMemberChat: () => void;
  /**
   * Open member chat and attach the reply context to a list item (e.g. context menu Comment).
   */
  openMemberChatForItemComment: (itemId: string) => void;
  /**
   * Close both chats
   */
  closeAllChats: () => void;
};

const useChatStore = create<ChatStore>((set) => ({
  isAIChatOpen: false,
  isAIBottomChatOpen: false,
  aiBottomChatHeightPx: 320,
  aiDrawerConversationId: null,
  aiBottomConversationId: null,
  isMemberChatOpen: false,
  currentFolderId: null,
  aiChatDraftMessage: '',
  aiChatDraftFocusRequestId: 0,
  memberChatDraftMessage: '',
  memberChatDraftFocusRequestId: 0,
  memberChatReplyTarget: null,
  jumpToMessage: null,

  setAIChatOpen: (open) => set({ isAIChatOpen: open }),
  setAIBottomChatOpen: (open) => set({ isAIBottomChatOpen: open }),
  setAIBottomChatHeightPx: (heightPx) => set({ aiBottomChatHeightPx: Math.max(180, Math.min(700, heightPx)) }),
  setAIDrawerConversationId: (conversationId) => set({ aiDrawerConversationId: conversationId }),
  setAIBottomConversationId: (conversationId) => set({ aiBottomConversationId: conversationId }),
  setMemberChatOpen: (open) => set({ isMemberChatOpen: open }),
  setCurrentFolderId: (folderId) => set({ currentFolderId: folderId }),

  setAIChatDraftMessage: (text) => set({ aiChatDraftMessage: text }),

  pushAIChatDraftMessage: (text) =>
    set({
      aiChatDraftMessage: text,
      aiChatDraftFocusRequestId: Date.now(),
    }),

  appendAIChatDraftMessage: (text) =>
    set((state) => {
      const trimmed = text.trim();
      if (!trimmed) return state;
      const nextDraft = state.aiChatDraftMessage
        ? `${state.aiChatDraftMessage}\n\n${trimmed}`
        : trimmed;
      return {
        aiChatDraftMessage: nextDraft,
        aiChatDraftFocusRequestId: Date.now(),
      };
    }),

  clearAIChatDraftMessage: () => set({ aiChatDraftMessage: '' }),

  setMemberChatDraftMessage: (text) => set({ memberChatDraftMessage: text }),

  pushMemberChatDraftMessage: (text) =>
    set({
      memberChatDraftMessage: text,
      memberChatDraftFocusRequestId: Date.now(),
    }),

  appendMemberChatDraftMessage: (text) =>
    set((state) => {
      const trimmed = text.trim();
      if (!trimmed) return state;
      const nextDraft = state.memberChatDraftMessage
        ? `${state.memberChatDraftMessage}\n\n${trimmed}`
        : trimmed;
      return {
        memberChatDraftMessage: nextDraft,
        memberChatDraftFocusRequestId: Date.now(),
      };
    }),

  clearMemberChatDraftMessage: () => set({ memberChatDraftMessage: '' }),

  requestMemberChatComposerFocus: () =>
    set({ memberChatDraftFocusRequestId: Date.now() }),

  setMemberChatReplyTarget: (target) =>
    set({
      memberChatReplyTarget: target,
      memberChatDraftFocusRequestId: Date.now(),
    }),

  clearMemberChatReplyTarget: () => set({ memberChatReplyTarget: null }),

  requestMemberChatJumpToMessage: (messageId, source) =>
    set({
      jumpToMessage: {
        messageId: String(messageId),
        ...(source ? { source } : {}),
      },
    }),

  clearMemberChatJumpToMessage: () => set({ jumpToMessage: null }),

  toggleAIChat: () => set((state) => ({ 
    isAIChatOpen: !state.isAIChatOpen,
    // Don't close member chat - allow both to be open simultaneously
  })),
  toggleAIBottomChat: () => set((state) => ({
    isAIBottomChatOpen: !state.isAIBottomChatOpen,
  })),
  
  toggleMemberChat: () => set((state) => ({ 
    isMemberChatOpen: !state.isMemberChatOpen,
    // Don't close AI chat - allow both to be open simultaneously
  })),

  openMemberChatForItemComment: (itemId) => {
    set({
      isMemberChatOpen: true,
      memberChatDraftFocusRequestId: Date.now(),
    });
    useItemStore.getState().setCommentId(String(itemId));
  },
  
  closeAllChats: () => set({ 
    isAIChatOpen: false, 
    isAIBottomChatOpen: false,
    isMemberChatOpen: false 
  }),
}));

export default useChatStore;
